import { useEffect, useState } from 'react'
import { Check, ChevronDown, X } from 'lucide-react'

import { ProductModel } from '@/http/models/Product/Product'
import { ProductModel as ProductModelType } from '@/types'
import { useDebouncedCallback } from '@/hooks'
import { getFileUrl } from '@/clients/http'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Command, CommandGroup, CommandInput, CommandItem, CommandList, CommandEmpty } from '@/components/ui/command'
import { ContentLoader } from '@/components/atoms'
import { fieldClass } from '@/components/ui/field-class'
import { cn } from '@/lib/utils'

interface Props {
  selectedIds: string[]
  onChange: (ids: string[]) => void
  multiple?: boolean
  label?: string
}

export const CampaignProductPicker = ({ selectedIds, onChange, multiple = false, label }: Props) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [products, setProducts] = useState<ProductModelType.Product[]>([])
  const [loading, setLoading] = useState(false)
  // Selected products may not be in the current search results, so their
  // labels are cached here as they're encountered (fetched or selected).
  const [knownProducts, setKnownProducts] = useState<Record<string, ProductModelType.Product>>({})

  const fetchProducts = async (q: string) => {
    setLoading(true)
    const response = await ProductModel.getList({
      query: { search: q || undefined, length: 20 },
    })
    if (response.success && response.data?.result) {
      setProducts(response.data.result)
      setKnownProducts((prev) => {
        const next = { ...prev }
        response.data!.result.forEach((p) => { next[p._id] = p })
        return next
      })
    }
    setLoading(false)
  }

  const debouncedFetch = useDebouncedCallback(fetchProducts, 400)

  useEffect(() => {
    if (open) fetchProducts(search)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleSearch = (value: string) => {
    setSearch(value)
    debouncedFetch(value)
  }

  const toggleProduct = (id: string) => {
    if (multiple) {
      onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id])
    } else {
      onChange(selectedIds.includes(id) ? [] : [id])
      setOpen(false)
    }
  }

  const selectedProducts = selectedIds.map((id) => knownProducts[id]).filter(Boolean) as ProductModelType.Product[]

  return (
    <div className="space-y-1.5">
      {label && <p className="text-sm text-ink">{label}</p>}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(fieldClass, 'h-auto min-h-8 justify-between gap-2 py-1 font-normal hover:bg-card')}>
            <div className="flex flex-wrap gap-1.5 overflow-hidden">
              {selectedProducts.length > 0 ? (
                selectedProducts.map((p) => (
                  <Badge
                    key={p._id}
                    tone="soft"
                    className="gap-1 text-ink"
                  >
                    {p.name}
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleProduct(p._id)
                      }}
                      className="ml-0.5 rounded-full outline-none"
                    >
                      <X className="size-3 text-muted-foreground hover:text-ink" />
                    </button>
                  </Badge>
                ))
              ) : (
                <span className="text-placeholder-foreground">Search products…</span>
              )}
            </div>
            <ChevronDown className={cn('shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput placeholder="Search products…" value={search} onValueChange={handleSearch} />
            <CommandList className="max-h-64 overflow-y-auto">
              {loading ? (
                <ContentLoader classes={{ container: 'h-24' }} showText={false} />
              ) : products.length === 0 ? (
                <CommandEmpty>No products found</CommandEmpty>
              ) : (
                <CommandGroup>
                  {products.map((product) => {
                    const selected = selectedIds.includes(product._id)
                    return (
                      <CommandItem
                        key={product._id}
                        value={product._id}
                        onSelect={() => toggleProduct(product._id)}
                        className="gap-2"
                      >
                        <span
                          className={cn(
                            'flex size-4 shrink-0 items-center justify-center rounded border',
                            selected ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-card',
                          )}
                        >
                          {selected && <Check className="size-3" />}
                        </span>
                        {product.thumbnail?.path && (
                          <img
                            src={getFileUrl(product.thumbnail.path)}
                            alt={product.name}
                            className="size-6 shrink-0 rounded border border-border-subtle object-cover"
                          />
                        )}
                        <span className="truncate">{product.name}</span>
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
